// packages/api/src/ApiService.ts
import { OpResult } from "@sdflc/api-helpers";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { isAuthErrorCode, triggerAuthError } from "./authErrorHandler";
import { getApiClient, getOnClearSession } from "./configure";
import { hasSessionRefresher, refreshSessionOnce, sessionNeedsRenewal } from "./sessionRefresh";
import { findRequestTimeout, isRequestTimeout, requestTimeoutMessage } from "./requestTimeout";
import { notifyWriteTimeout } from "./writeTimeoutSignal";

/**
 * ROADMAP #239 — what one call learned about its session. Collected instead of fired: the call first REFRESHES an expired
 * session and repeats once (`call`), and only a refusal that survives that reaches the auth handler.
 */
interface AuthRefusal {
  code: number;
  message: string;
}

type AuthSink = { refusal: AuthRefusal | null };

/**
 * Options this service reads itself and never passes on to Apollo.
 * • `sessionRetry` (default true) — on an auth refusal, refresh the session once and repeat the call. Off for the refresh
 *   itself (it would wait on itself).
 * • `reportAuthRefusal` (default true) — hand a surviving refusal to the auth handler. Off for the refresh too: its failure
 *   is reported once, by the call that asked for it.
 */
export interface ApiCallOptions {
  sessionRetry?: boolean;
  reportAuthRefusal?: boolean;
}

/**
 * Generic API service — wraps every GraphQL operation in an OpResult.
 *
 * Two error channels are normalized to the SAME OpResult shape
 * ({ code, data, errors: [{ name, errors }] }) so the UI never has to know
 * where an error came from:
 *
 *  1. Operation-level errors — our API returns them inside the payload
 *     (`data.tagList.errors`). These already match OpResult.
 *  2. Transport / schema / validation errors — GraphQL puts these in the
 *     response's TOP-LEVEL `errors` array (e.g. "Cannot query field X on type
 *     Y"). Because the Apollo client runs `errorPolicy: "all"`, these do NOT
 *     throw: they arrive with `data: null`. We surface their real messages
 *     instead of a generic "no data" fallback.
 *
 * A thrown ApolloError (errorPolicy overridden, or a hard network failure) is
 * funneled through the same mappers.
 */
export class ApiService {
  // ==========================================================================
  // Shared error normalizers
  // ==========================================================================

  /**
   * True when a GraphQL error means the SESSION ITSELF is no good — the only kind that may sign the
   * user out.
   *
   * ⚠ `FORBIDDEN` is deliberately NOT one of them. "Authenticated but not allowed to do this" is an
   * ordinary refusal that belongs in the form the user is looking at; treating it as a dead session
   * destroyed a perfectly good login and threw away what they had typed. That is exactly what
   * happened to team invitations: ms_auth refused the invite, the app signed the owner out and
   * bounced them to /account/signin, and the invitation was lost with no message ever shown.
   *
   * `authErrorHandler`'s own `AUTH_ERROR_CODES` had already excluded `OP_RESULT_CODES.FORBIDDEN` on
   * purpose (it is commented out there) — but this function laundered a GraphQL FORBIDDEN into
   * `triggerAuthError(UNAUTHORIZED)`, which IS in that set, so the exclusion never held.
   */
  private static isSessionGqlError(gqlError: any): boolean {
    return gqlError?.extensions?.code === "UNAUTHENTICATED";
  }

  /** Authenticated, but not permitted. Surfaced to the caller; never a sign-out. */
  private static isForbiddenGqlError(gqlError: any): boolean {
    return gqlError?.extensions?.code === "FORBIDDEN";
  }

  /**
   * Dig GraphQL-error objects out of whatever shape Apollo hands us. Depending
   * on the server's HTTP status and the client's errorPolicy, the same logical
   * error can arrive as:
   *   - response.errors            (errorPolicy "all", HTTP 200)
   *   - error.graphQLErrors        (thrown, HTTP 200 with errors)
   *   - error.networkError.result.errors  (thrown ServerError, e.g. HTTP 400
   *     GRAPHQL_VALIDATION_FAILED — the "Cannot query field X" case)
   *   - error.networkError.bodyText (unparsed JSON body)
   * Returns a flat array of { message, extensions } items (possibly empty).
   */
  private static gqlErrorsFrom(source: any, depth = 0): any[] {
    if (!source || depth > 4) return [];
    if (Array.isArray(source)) return source;

    if (Array.isArray(source.errors) && source.errors.length > 0) {
      return source.errors;
    }
    if (Array.isArray(source.graphQLErrors) && source.graphQLErrors.length > 0) {
      return source.graphQLErrors;
    }
    // ApolloQueryResult.error / thrown ApolloError (singular) — client.query()
    // often attaches the combined error here rather than on `.errors`.
    if (source.error) {
      const nested = this.gqlErrorsFrom(source.error, depth + 1);
      if (nested.length > 0) return nested;
    }
    if (source.result) {
      const nested = this.gqlErrorsFrom(source.result, depth + 1);
      if (nested.length > 0) return nested;
    }
    if (source.networkError) {
      const nested = this.gqlErrorsFrom(source.networkError, depth + 1);
      if (nested.length > 0) return nested;
    }
    if (typeof source.bodyText === "string") {
      try {
        const parsed = JSON.parse(source.bodyText);
        const nested = this.gqlErrorsFrom(parsed, depth + 1);
        if (nested.length > 0) return nested;
      } catch {
        /* not JSON — ignore */
      }
    }
    return [];
  }

  /** Map GraphQL errors to OpResult error items, keeping the real messages. */
  private static mapGqlErrors(
    gqlErrors: any[]
  ): { name: string; errors: string[] }[] {
    return (gqlErrors || []).map((gqlError: any) => ({
      // Our field-level validation comes through the payload, so top-level
      // errors are usually field-less; honor `extensions.field` if present.
      name: gqlError?.extensions?.field || "",
      errors: [gqlError?.message || "Unexpected server error"],
    }));
  }

  /**
   * The HTTP status a failed request came back with, if any. Apollo 4 throws (or resolves carrying) a `ServerError` with
   * `statusCode` on the error itself; Apollo 3 wrapped it in `networkError`. ⚠ Read both: after the upgrade the code
   * read only the Apollo 3 shape, so no HTTP status was ever seen.
   */
  private static httpStatusOf(error: any, depth = 0): number | undefined {
    if (!error || typeof error !== "object" || depth > 4) {
      return undefined;
    }

    if (typeof error.statusCode === "number") {
      return error.statusCode;
    }

    for (const inner of [error.networkError, error.cause, error.error]) {
      const status = this.httpStatusOf(inner, depth + 1);
      if (status !== undefined) {
        return status;
      }
    }

    return undefined;
  }

  /**
   * An HTTP 401 is the session itself: hand it to the ONE global handler, which skips a crew session and asks `me()`
   * before signing anyone out. A 403 is a refusal (see `isSessionGqlError`) and never a sign-out.
   */
  private static handleAuthFromStatus(status: number | undefined, sink: AuthSink): void {
    if (status === 401 && !sink.refusal) {
      sink.refusal = { code: OP_RESULT_CODES.UNAUTHORIZED, message: "Session expired. Please sign in again." };
    }
  }

  /** The OpResult code an HTTP status decides, when it decides one. */
  private static codeFromStatus(status: number | undefined): number | undefined {
    if (status === 401) {
      return OP_RESULT_CODES.UNAUTHORIZED;
    }

    if (status === 403) {
      return OP_RESULT_CODES.FORBIDDEN;
    }

    return undefined;
  }

  /** Trigger the global auth handler only when the SESSION is the problem. */
  private static handleAuthFromGqlErrors(gqlErrors: any[], sink: AuthSink): void {
    const sessionError = (gqlErrors || []).find((e: any) =>
      this.isSessionGqlError(e)
    );
    if (sessionError && !sink.refusal) {
      sink.refusal = { code: OP_RESULT_CODES.UNAUTHORIZED, message: sessionError.message };
    }
  }

  /**
   * Pick an OpResult code for a batch of top-level GraphQL errors. A refusal keeps its own identity:
   * FORBIDDEN stays FORBIDDEN so the UI can say "you are not allowed to do that" instead of
   * pretending the user is signed out.
   */
  private static codeFromGqlErrors(gqlErrors: any[]): number {
    const errors = gqlErrors || [];
    if (errors.some((e: any) => this.isSessionGqlError(e))) {
      return OP_RESULT_CODES.UNAUTHORIZED;
    }
    if (errors.some((e: any) => this.isForbiddenGqlError(e))) {
      return OP_RESULT_CODES.FORBIDDEN;
    }

    return OP_RESULT_CODES.FAILED;
  }

  /**
   * Note an operation-level auth refusal on the call's sink (`call` decides what to do with it).
   */
  private static checkAuthError(
    result: {
      code?: number;
      errors?: any[];
    },
    sink: AuthSink
  ): void {
    const code = result.code ?? OP_RESULT_CODES.OK;
    if (isAuthErrorCode(code) && !sink.refusal) {
      const message =
        result.errors?.[0]?.errors?.[0] || "Authentication required";
      sink.refusal = { code, message };
    }
  }

  // ==========================================================================
  // Unified response / exception processing
  // ==========================================================================

  /**
   * Turn a GraphQL response into an OpResult. Prefers the operation's own
   * OpResult payload; falls back to the top-level GraphQL errors; only then to
   * a generic empty-response error.
   */
  private static processResponse(response: any, sink: AuthSink): OpResult {
    // A timed-out request can also come back RESOLVED, carrying its error (errorPolicy "all").
    if (isRequestTimeout(response?.error)) {
      return this.timeoutResult(response.error);
    }

    const opName = Object.keys(response?.data || {})[0];
    const result: any = opName ? response.data?.[opName] : null;

    // 1. Operation-level OpResult (our API's normal, and error, shape).
    if (
      result &&
      (result.code !== undefined ||
        result.data !== undefined ||
        result.errors !== undefined)
    ) {
      this.checkAuthError(result, sink);
      const op = new OpResult({
        code: result.code ?? OP_RESULT_CODES.OK,
        data: result.data,
        errors: result.errors || [],
      });

      // Carry the paginated total when the query selected it (list results).
      if (typeof result.total === "number") {
        op.setTotal(result.total);
      }

      return op;
    }

    // 2. No usable payload — surface the real transport/schema/validation
    //    errors instead of a generic "no data" message. A resolved result can carry an HTTP failure too
    //    (errorPolicy "all"), so its status counts the same as a thrown one's.
    const status = this.httpStatusOf(response?.error);
    this.handleAuthFromStatus(status, sink);
    const topLevelErrors = this.gqlErrorsFrom(response);
    if (topLevelErrors.length > 0) {
      this.handleAuthFromGqlErrors(topLevelErrors, sink);
      return new OpResult({
        code: this.codeFromStatus(status) ?? this.codeFromGqlErrors(topLevelErrors),
        data: null,
        errors: this.mapGqlErrors(topLevelErrors),
      });
    }

    // 3. Last resort: a combined ApolloError may carry only a `.message`.
    const apolloMessage: string | undefined =
      response?.error?.message ||
      response?.error?.graphQLErrors?.[0]?.message ||
      response?.errors?.[0]?.message;
    if (apolloMessage) {
      return new OpResult({
        code: this.codeFromStatus(status) ?? OP_RESULT_CODES.FAILED,
        data: null,
        errors: [{ name: "", errors: [apolloMessage] }],
      });
    }

    // 4. Genuinely empty response with no error detail anywhere. Log the raw
    //    response so it can be inspected, and say which operation it was.
    console.warn(
      `[ApiService] Empty GraphQL response for "${opName || "operation"}" with no errors:`,
      response
    );
    return new OpResult({
      code: OP_RESULT_CODES.FAILED,
      data: null,
      errors: [
        {
          name: "",
          errors: [
            `The server returned no data for "${
              opName || "the request"
            }" and no error details. See the browser console for the raw response.`,
          ],
        },
      ],
    });
  }

  /**
   * ROADMAP #122 — a request the transport abandoned (`createTimeoutFetch`): an ordinary refusal. A write's message does
   * not claim it failed — the server may have finished it after we stopped listening; a read's says try again. Nameless,
   * so a form shows it in its footer, never on a field.
   */
  private static timeoutResult(error: unknown): OpResult {
    // A write may have landed after all: every list on screen re-reads (ROADMAP #131). Unknown counts as a write.
    if (findRequestTimeout(error)?.isWrite !== false) {
      notifyWriteTimeout();
    }

    return new OpResult({
      code: OP_RESULT_CODES.NETWORK_ERROR,
      data: null,
      errors: [{ name: "", errors: [requestTimeoutMessage(error)] }],
    });
  }

  /**
   * Turn a thrown ApolloError (errorPolicy overridden, or a hard network
   * failure) into an OpResult, using the same mappers as processResponse.
   */
  private static processException(error: any, sink: AuthSink): OpResult {
    console.error("GraphQL operation error:", error);

    if (isRequestTimeout(error)) {
      return this.timeoutResult(error);
    }

    const statusCode = this.httpStatusOf(error);
    this.handleAuthFromStatus(statusCode, sink);

    // Pull structured GraphQL errors from anywhere they might hide (thrown
    // graphQLErrors, networkError.result.errors from an HTTP 400 validation
    // error, an unparsed bodyText, …) and surface their real messages.
    const gqlErrors = this.gqlErrorsFrom(error);
    if (gqlErrors.length > 0) {
      this.handleAuthFromGqlErrors(gqlErrors, sink);
      return new OpResult({
        code: this.codeFromStatus(statusCode) ?? this.codeFromGqlErrors(gqlErrors),
        data: null,
        errors: this.mapGqlErrors(gqlErrors),
      });
    }

    // A network failure with no structured body (connection refused, CORS,
    // timeout), or an HTTP failure (Apollo 4's ServerError is the error itself). Include the HTTP status when we have one.
    if (error?.networkError || statusCode !== undefined) {
      const statusText = statusCode ? ` (HTTP ${statusCode})` : "";
      return new OpResult({
        code: this.codeFromStatus(statusCode) ?? OP_RESULT_CODES.NETWORK_ERROR,
        data: null,
        errors: [
          {
            name: "",
            errors: [
              `${error.networkError?.message || error.message || "Network error"}${statusText}`,
            ],
          },
        ],
      });
    }

    // Unknown/JS error.
    return new OpResult({
      code: OP_RESULT_CODES.EXCEPTION,
      data: null,
      errors: [
        { name: "", errors: [error?.message || "Unknown error occurred"] },
      ],
    });
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * ROADMAP #239 — run one operation; when its session turned out EXPIRED (any auth refusal — the services do not tell an
   * expired token from a refused one), renew it once and repeat the operation once. Only a refusal that survives that —
   * or one met with no way to renew (a site without a refresher, a crew session) — reaches the auth handler, which asks
   * `me()` before it signs anyone out. ⓘ Repeating is safe: an auth refusal is answered before the operation runs.
   */
  private static async call(exec: (apolloOptions: any) => Promise<any>, options: any): Promise<OpResult> {
    const { sessionRetry = true, reportAuthRefusal = true, ...apolloOptions } = (options || {}) as ApiCallOptions & Record<string, any>;
    const once = async (): Promise<[OpResult, AuthSink]> => {
      const sink: AuthSink = { refusal: null };

      try {
        return [this.processResponse(await exec(apolloOptions), sink), sink];
      } catch (error: any) {
        return [this.processException(error, sink), sink];
      }
    };
    const canRenew = sessionRetry && hasSessionRefresher();
    let renewFailed = false;

    // ⚠ Renew FIRST when the token is known to be (nearly) expired: the `tryAuth` actions answer an expired token as
    //   ANONYMOUS (empty data), not with a refusal — the path below would never see them (see sessionRefresh.ts).
    if (canRenew && sessionNeedsRenewal()) {
      renewFailed = !(await refreshSessionOnce(Date.now()));
    }

    // The renewal must be NEWER than this request for its cookie to be the new one (`refreshSessionOnce`).
    const sentAt = Date.now();
    let [result, sink] = await once();

    // A renewal that just failed is not tried again for the same call — its refusal goes to the handler.
    if (sink.refusal && canRenew && !renewFailed && (await refreshSessionOnce(sentAt))) {
      [result, sink] = await once();
    }

    if (sink.refusal && reportAuthRefusal) {
      triggerAuthError(sink.refusal.code, sink.refusal.message);
    }

    return result;
  }

  /** Execute a GraphQL mutation and wrap the result in OpResult. */
  static async executeMutation(
    mutation: any,
    variables: any = {},
    options: any = {}
  ): Promise<OpResult> {
    return this.call((apolloOptions) => getApiClient().mutate({ mutation, variables, ...apolloOptions }), options);
  }

  /** Execute a GraphQL query and wrap the result in OpResult. */
  static async executeQuery(
    query: any,
    variables: any = {},
    options: any = {}
  ): Promise<OpResult> {
    return this.call(
      (apolloOptions) =>
        getApiClient().query({
          query,
          variables,
          ...apolloOptions,
          fetchPolicy: apolloOptions.fetchPolicy || "network-only", // Allow override
        }),
      options
    );
  }

  /**
   * Clear user session data. The app-specific teardown (e.g. removing the
   * cached user from localStorage) is injected via `configureApiService`;
   * cookies are handled by the server. Also resets the GraphQL client cache.
   */
  static clearSession() {
    getOnClearSession()?.();
    // Reset the client cache to clear any cached user data.
    getApiClient().resetStore?.();
  }
}
